import type { EntityType, FieldDef, FormDef, FormKey, TabDef } from './types.js'
import { formByKey, forms, fieldsByPathByForm, tabsByForm } from './generated/index.js'

/**
 * Which form an entity fills in. Companies report the company profile; a fund manager
 * (GP) and a limited partner (LP) each have their own. The mapping is one to one today,
 * and lives here so nothing downstream has to know that.
 */
export function formKeyFor(entityType: EntityType | string | null | undefined): FormKey {
  switch (entityType) {
    case 'gp':
      return 'gp'
    case 'lp':
      return 'lp'
    default:
      return 'company'
  }
}

export function formFor(entityType: EntityType | string | null | undefined): FormDef {
  return formByKey[formKeyFor(entityType)]!
}

/** The tabs of a form, by form key. Falls back to the company form for an unknown key. */
export function tabsForForm(formKey: FormKey | string | null | undefined): TabDef[] {
  return tabsByForm[String(formKey)] ?? tabsByForm['company']!
}

/** The tabs an entity of this kind fills in. */
export function tabsForEntity(entityType: EntityType | string | null | undefined): TabDef[] {
  return tabsForForm(formKeyFor(entityType))
}

/**
 * The tabs one cycle uses. An empty request means the full form without its campaign tabs.
 * A narrowed request keeps form order and always adds the review tab (CYC-09).
 */
export function tabsForCycle(formKey: FormKey | string | null | undefined, requestedTabs: readonly string[]): TabDef[] {
  const all = tabsForForm(formKey)
  if (requestedTabs.length === 0) return all.filter((t) => !t.onlyWhenRequested)
  return all.filter((t) => requestedTabs.includes(t.key) || t.key === 'review')
}

export function tabByKeyForForm(formKey: FormKey | string | null | undefined, tabKey: string): TabDef | undefined {
  return tabsForForm(formKey).find((t) => t.key === tabKey)
}

/** Every field of a form keyed by `tab.section.field`. */
export function fieldsForForm(formKey: FormKey | string | null | undefined): Record<string, FieldDef> {
  return fieldsByPathByForm[String(formKey)] ?? fieldsByPathByForm['company']!
}

/** Every form, in sidebar order: company, GP, LP. */
export function allForms(): FormDef[] {
  return forms
}

/** The noun to call this entity in prose — emails, page headings, help text. */
export function entityNoun(entityType: EntityType | string | null | undefined): string {
  switch (entityType) {
    case 'gp':
      return 'fund manager'
    case 'lp':
      return 'limited partner'
    default:
      return 'company'
  }
}

/** True for the entity kinds the admin lists under Funds rather than Companies. */
export function isFundEntity(entityType: EntityType | string | null | undefined): boolean {
  return entityType === 'gp' || entityType === 'lp'
}
